import { type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';

import { createSearchMatcher } from './filterSearch';
import { FilterSearchField } from './FilterSearchField';

interface SearchableCheckboxListProps<T> {
  items: T[];
  getSearchTerms: (item: T) => string[];
  getGroup?: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  placeholder: string;
  searchText: string;
  onSearchChange: (value: string) => void;
}

const groupItems = <T,>(items: T[], getGroup: (item: T) => string): [string, T[]][] => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = getGroup(item);
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  });
  return [...groups.entries()];
};

export const SearchableCheckboxList = <T,>({
  items,
  getSearchTerms,
  getGroup,
  renderItem,
  placeholder,
  searchText,
  onSearchChange,
}: SearchableCheckboxListProps<T>) => {
  const { t } = useTranslation();

  const matches = createSearchMatcher(searchText);
  const matching = items.filter((item) => matches(getSearchTerms(item)));

  const renderMatches = () => {
    if (matching.length === 0) {
      return <FilterEmptyState text={t('common.no_results')} />;
    }
    if (getGroup) {
      return (
        <div className="space-y-2">
          {groupItems(matching, getGroup).map(([group, groupMembers]) => (
            <div key={group}>{groupMembers.map(renderItem)}</div>
          ))}
        </div>
      );
    }
    return <div>{matching.map(renderItem)}</div>;
  };

  return (
    <div className="space-y-3">
      <FilterSearchField value={searchText} onChange={onSearchChange} placeholder={placeholder} />

      {renderMatches()}
    </div>
  );
};
